/**
 * The wire shape of a sheet as the rows layer reads it: `RawSheet`, its
 * `RawRow`s and their seed `RawCell`s, with the JSON-boundary readers that
 * brand and check them (`parseRawSheet`, `parseRawRow`).
 *
 * Declared at the wire rank so a backend (which fetches and writes these
 * shapes) names them without importing the hydrator above the sheet. The
 * package publishes them as `RawSheet` / `RawRow` / `RawCell`
 * (`../raw/raw-sheet.ts`, whose namespaces carry `parse` and the hydrate
 * helpers).
 */

import { NaiveDate } from "@tento-chrono";
import { Model } from "@tento-core/model";
import type { CellValue } from "../core/dtype.ts";
import { Json, type JsonObject } from "../core/json.ts";
import { CellId } from "../ids/ids.ts";
import { RowNetworkKey } from "../ids/row-network-key.ts";
import { SheetNetworkKey } from "../ids/sheet-network-key.ts";
import { WireDecodeError } from "./wire-decode.ts";

/**
 * Seed cell shape carried on `RawRow.cells`. Dates arrive in
 * `NaiveDate.Partial`'s string form (e.g. `"ymd-2026-4-18"`); the
 * hydrator parses them, decodes wire values through the row's
 * declared `meta.encoding` (if any), and installs each surviving
 * cell into `SourceRowCellData` without emitting (the sink would
 * otherwise fire spurious `ColumnDataChanged` events before any
 * subscriber has a chance to attach).
 */
export interface RawCell {
  readonly date: string;
  /** Wire JSON from a fixture or snapshot; a hydrated value on `serialize`. */
  readonly value: Json | CellValue;
  readonly cellId?: CellId;
}

/** Minimal shape of a server-emitted row the hydrator needs. */
export interface RawRow {
  readonly id: RowNetworkKey;
  readonly type: string;
  readonly label?: string;
  readonly formula?: string;
  readonly lookupKey?: string;
  readonly timeScale?: string;
  readonly heightPx?: number;
  /**
   * Server-owned metadata bag (mirrors Rust `SheetRowDb.metadata`
   * JSONB column). Holds bookkeeping keys the server manages —
   * notably `encoding: string` (wire-format decoder id for this
   * row's cells). Distinct from `attributes`, which is the
   * user-visible config bag. Read-only from the client's
   * perspective.
   */
  readonly meta?: JsonObject;
  /**
   * Parent row's id. Omitted → row is a sheet-root child. Used by
   * `hydrate` to seed `TreeStructure`.
   *
   * Dual to `children`: `parentId` is child→parent, `children` is
   * parent→kids. The wire format uses `children` (matches
   * `SheetsNetwork.SheetRow.children`); `parentId` is the historical
   * rows-side representation. Hydrate accepts either and reconciles.
   * Inconsistency (both forms set for the same edge with disagreeing
   * answers) throws.
   */
  readonly parentId?: RowNetworkKey;
  /**
   * Child row ids declared by this row (parent → kids). Mirrors the
   * server wire format `SheetsNetwork.SheetRow.children`. `hydrate` inverts
   * this into `parentId` edges before seeding `TreeStructure`. Sibling
   * order in the resulting tree comes from this array's order.
   */
  readonly children?: readonly RowNetworkKey[];
  /**
   * Free-form attributes the descriptor dispatcher may consult
   * (`cardinality`, `rendererId`, `template`, `props`, `filter`, …).
   * Short-format → RawSheet bridge populates this from fixture attrs.
   */
  readonly attributes?: JsonObject;
  /**
   * Inline seed cells. Source rows only — layouts/renderers/aliases
   * ignore this field. Populated by fixtures (`*-seeded.sheet.json`)
   * and by real backends that return wire snapshots with cell data.
   */
  readonly cells?: readonly RawCell[];
}

/** Minimal shape of a server-emitted sheet. */
export interface RawSheet {
  readonly id: SheetNetworkKey;
  readonly title?: string;
  /**
   * Reserved SHEET key (`:journal`, `:~lona:home`, …) when this sheet is a
   * builtin — mirrors `SheetsNetwork.UserSheet.lookupKey`.
   *
   * Load-bearing across the ephemeral→promoted round trip: a promoted builtin
   * arrives addressed by its server id (`ss_<hex>`), so without this field the
   * rows layer loses the builtin identity the moment the sheet crosses the
   * package boundary and has to GUESS it from the title + a row's lookup key
   * (see `SheetModelCache` `builtinLookupKeyForRawSheet`) — a guess that fails
   * exactly when the promoted row came back without its `lookupKey`, which is
   * how the Journal row lost its 400px normalization on reload.
   *
   * Absent for ordinary user sheets and for templates (a template is a
   * definition WITHOUT a reserved key — see `sheet-definition.ts`).
   */
  readonly lookupKey?: string;
  /**
   * Sheet-level display width override (pixels). Absent → the
   * viewport picks its own width. Persisted for user-adjustable
   * multi-sheet layouts.
   */
  readonly width?: number;
  /**
   * Current user's access role for this sheet — drives
   * `Sheet.editable`. Absent defaults to `"owner"` (standalone
   * / test sheets are editable). Mirrors
   * `SheetsNetwork.UserSheet.accessRole`.
   */
  readonly accessRole?: "owner" | "editor" | "viewer";
  /**
   * Sheet's active timescale (`"day"` / `"week"` / `"month"` /
   * `"year"`). Drives `AliasTemplate.activeLayout`-based layout-row
   * synthesis at hydrate time. Absent opens at `"day"`; so does a spelling
   * that is not a timescale, which hydrate reports as a decode issue
   * (`raw-sheet/time-scale-decode`).
   */
  readonly timeScale?: string;
  readonly rows: readonly RawRow[];
}

/**
 * `RawSheet` read at its JSON boundary (a `*.sheet.json` fixture, a stored
 * snapshot): every row through `parseRawRow` (ids branded verbatim), the
 * sheet's own fields type-checked. The sheet `id` keeps its spelling, as a
 * `SheetNetworkKey` (the text every route takes). A mismatch is a
 * thrown `WireDecodeError` naming `RawSheet` and the path.
 */
export function parseRawSheet(raw: Json): RawSheet {
  const obj = requireObject("RawSheet", raw, []);
  const rows = obj["rows"];
  if (!Array.isArray(rows)) throw decodeError("RawSheet", ["rows"], "expected an array");
  const accessRole = optionalString("RawSheet", obj, "accessRole", []);
  if (accessRole !== undefined && accessRole !== "owner" && accessRole !== "editor" && accessRole !== "viewer") {
    throw decodeError("RawSheet", ["accessRole"], "expected owner, editor or viewer");
  }
  const title = optionalString("RawSheet", obj, "title", []);
  const lookupKey = optionalString("RawSheet", obj, "lookupKey", []);
  const width = optionalNumber("RawSheet", obj, "width", []);
  const timeScale = optionalString("RawSheet", obj, "timeScale", []);
  return {
    id: SheetNetworkKey.fromWire(requireString("RawSheet", obj, "id", [])),
    ...(title !== undefined ? { title } : {}),
    ...(lookupKey !== undefined ? { lookupKey } : {}),
    ...(width !== undefined ? { width } : {}),
    ...(accessRole !== undefined ? { accessRole } : {}),
    ...(timeScale !== undefined ? { timeScale } : {}),
    rows: checkRowGraph(rows.map((row: Json, i: number) => parseRawRowAt(row, ["rows", String(i)]))),
  };
}
/**
 * `RawRow` read at its JSON boundary (a fixture, a snapshot, a response):
 * brands its ids (`id`, `parentId`, `children` as `RowNetworkKey`,
 * verbatim — no canonicalization) and checks every field's type. A field of
 * the wrong type, a missing `id` / `type`, or an empty id is a thrown
 * `WireDecodeError` naming `RawRow` and the path.
 */
export function parseRawRow(raw: Json): RawRow {
  return parseRawRowAt(raw, []);
}

/** Types and values that go with `RawRow`: `parse`. */
export namespace RawRow {
  /**
   * `RawRow` read at its JSON boundary (a fixture, a snapshot, a response):
   * brands its ids (`id`, `parentId`, `children` as `RowNetworkKey`,
   * verbatim — no canonicalization) and checks every field's type. A field of
   * the wrong type, a missing `id` / `type`, or an empty id is a thrown
   * `WireDecodeError` naming `RawRow` and the path.
   */
  export function parse(raw: Json): RawRow {
    return parseRawRowAt(raw, []);
  }
}

// ── JSON-boundary readers of `RawRow.parse` / `RawSheet.parse` ─────────

function decodeError(type: string, path: readonly string[], message: string): WireDecodeError {
  return new WireDecodeError(type, [Model.issue([...path], "type", message)]);
}

function requireObject(type: string, raw: Json, path: readonly string[]): JsonObject {
  const obj = Json.object(raw);
  if (obj == null) throw decodeError(type, path, "expected an object");
  return obj;
}

function requireString(type: string, obj: JsonObject, key: string, path: readonly string[]): string {
  const value = obj[key];
  if (typeof value !== "string") throw decodeError(type, [...path, key], "expected a string");
  return value;
}

/** An optional field: absent (or `null`) reads as `undefined`; any other non-string is malformed. */
function optionalString(type: string, obj: JsonObject, key: string, path: readonly string[]): string | undefined {
  const value = obj[key];
  if (value === undefined || value === null) return undefined;
  if (typeof value !== "string") throw decodeError(type, [...path, key], "expected a string");
  return value;
}

function optionalNumber(type: string, obj: JsonObject, key: string, path: readonly string[]): number | undefined {
  const value = obj[key];
  if (value === undefined || value === null) return undefined;
  if (typeof value !== "number") throw decodeError(type, [...path, key], "expected a number");
  return value;
}

function optionalObject(type: string, obj: JsonObject, key: string, path: readonly string[]): JsonObject | undefined {
  const value = obj[key];
  if (value === undefined || value === null) return undefined;
  const child = Json.object(value);
  if (child == null) throw decodeError(type, [...path, key], "expected an object");
  return child;
}

function rowKeyOf(type: string, raw: Json, path: readonly string[]): RowNetworkKey {
  if (typeof raw !== "string") throw decodeError(type, path, "expected a string");
  const key = RowNetworkKey.parse(raw);
  if (key == null) throw decodeError(type, path, "expected a non-empty row id");
  return key;
}

/**
 * `rows`, refused (a `WireDecodeError` naming `RawSheet` and the path) when
 * two rows share an id — hydrate would keep one and drop the other — or the
 * parent edges (`parentId`, and `children` read child → parent) close a
 * cycle, which hydrate cannot place and drops silently.
 */
function checkRowGraph(rows: readonly RawRow[]): readonly RawRow[] {
  const at = new Map<string, number>();
  for (const [i, row] of rows.entries()) {
    const seen = at.get(row.id);
    if (seen != null) {
      throw decodeError("RawSheet", ["rows", String(i), "id"], `duplicate row id "${row.id}" (also rows.${seen}.id)`);
    }
    at.set(row.id, i);
  }
  const parentOf = new Map<string, string>();
  for (const row of rows) {
    if (row.parentId != null) parentOf.set(row.id, row.parentId);
    for (const child of row.children ?? []) if (!parentOf.has(child)) parentOf.set(child, row.id);
  }
  for (const [i, row] of rows.entries()) {
    const chain = new Set<string>([row.id]);
    for (let p = parentOf.get(row.id); p != null; p = parentOf.get(p)) {
      if (chain.has(p)) {
        throw decodeError("RawSheet", ["rows", String(i), "parentId"], `parent cycle through "${row.id}"`);
      }
      chain.add(p);
    }
  }
  return rows;
}

function parseRawCell(raw: Json, path: readonly string[]): RawCell {
  const obj = requireObject("RawRow", raw, path);
  if (!("value" in obj)) throw decodeError("RawRow", [...path, "value"], "missing required field");
  const cellId = optionalString("RawRow", obj, "cellId", path);
  const value = obj["value"];
  const date = requireString("RawRow", obj, "date", path);
  const parsed = NaiveDate.Partial.parse(date);
  if (!parsed.isOk || parsed.asOk() == null) {
    throw decodeError("RawRow", [...path, "date"], `expected a partial date (ymd-2026-4-18, …), got "${date}"`);
  }
  return {
    date,
    value: value === undefined ? null : value,
    ...(cellId !== undefined ? { cellId: CellId.of(cellId) } : {}),
  };
}

function parseRawRowAt(raw: Json, path: readonly string[]): RawRow {
  const obj = requireObject("RawRow", raw, path);
  const label = optionalString("RawRow", obj, "label", path);
  const formula = optionalString("RawRow", obj, "formula", path);
  const lookupKey = optionalString("RawRow", obj, "lookupKey", path);
  const timeScale = optionalString("RawRow", obj, "timeScale", path);
  const heightPx = optionalNumber("RawRow", obj, "heightPx", path);
  const meta = optionalObject("RawRow", obj, "meta", path);
  const attributes = optionalObject("RawRow", obj, "attributes", path);
  const parentRaw = obj["parentId"];
  const parentId = parentRaw === undefined || parentRaw === null
    ? undefined
    : rowKeyOf("RawRow", parentRaw, [...path, "parentId"]);
  const childrenRaw = obj["children"];
  let children: readonly RowNetworkKey[] | undefined;
  if (childrenRaw !== undefined && childrenRaw !== null) {
    if (!Array.isArray(childrenRaw)) throw decodeError("RawRow", [...path, "children"], "expected an array");
    children = childrenRaw.map((c: Json, i: number) => rowKeyOf("RawRow", c, [...path, "children", String(i)]));
  }
  const cellsRaw = obj["cells"];
  let cells: readonly RawCell[] | undefined;
  if (cellsRaw !== undefined && cellsRaw !== null) {
    if (!Array.isArray(cellsRaw)) throw decodeError("RawRow", [...path, "cells"], "expected an array");
    cells = cellsRaw.map((c: Json, i: number) => parseRawCell(c, [...path, "cells", String(i)]));
  }
  return {
    id: rowKeyOf("RawRow", obj["id"] ?? null, [...path, "id"]),
    type: requireString("RawRow", obj, "type", path),
    ...(label !== undefined ? { label } : {}),
    ...(formula !== undefined ? { formula } : {}),
    ...(lookupKey !== undefined ? { lookupKey } : {}),
    ...(timeScale !== undefined ? { timeScale } : {}),
    ...(heightPx !== undefined ? { heightPx } : {}),
    ...(meta !== undefined ? { meta } : {}),
    ...(parentId !== undefined ? { parentId } : {}),
    ...(children !== undefined ? { children } : {}),
    ...(attributes !== undefined ? { attributes } : {}),
    ...(cells !== undefined ? { cells } : {}),
  };
}
